<script>
// Per-row results of one finished import job (GET /properties/import/
// {job_id}) — shown right after an upload and when a past import is opened
// from History. Failed rows are listed first so problems aren't buried
// under hundreds of "created" rows, then anything a Replace import deleted.
const SHEET_ORDER = [
  'projects',
  'floor_plans',
  'features',
  'nearby',
  'gallery',
  'brochures',
];
const RESULT_ORDER = ['failed', 'deleted', 'created', 'updated', 'skipped'];

const RESULT_CLASS = {
  created: 'text-green-700 dark:text-green-400',
  updated: 'text-green-700 dark:text-green-400',
  skipped: 'text-slate-500 dark:text-slate-400',
  failed: 'text-red-600 dark:text-red-400',
  deleted:
    'inline-flex px-1.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

// Deleted rows have no Excel row (`row: null`) — they sort last.
const rowNumber = row => (row.row == null ? Infinity : row.row);

export default {
  name: 'ImportJobResults',
  props: {
    job: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isJobFailed() {
      return this.job.status === 'failed';
    },
    isReplace() {
      return this.job.mode === 'replace';
    },
    warnings() {
      return this.job.warnings || [];
    },
    sortedRows() {
      const rank = (list, value) => {
        const index = list.indexOf(value);
        return index === -1 ? list.length : index;
      };
      return [...(this.job.rows || [])].sort(
        (a, b) =>
          rank(RESULT_ORDER, a.result) - rank(RESULT_ORDER, b.result) ||
          rank(SHEET_ORDER, a.file) - rank(SHEET_ORDER, b.file) ||
          rowNumber(a) - rowNumber(b)
      );
    },
  },
  methods: {
    sheetLabel(file) {
      const key = `REAL_ESTATE.BULK_IMPORT.SHEET_${String(file).toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : file;
    },
    resultLabel(result) {
      const key = `REAL_ESTATE.BULK_IMPORT.RESULT_${String(
        result
      ).toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : result;
    },
    resultClass(result) {
      return RESULT_CLASS[result] || '';
    },
    rowLabel(row) {
      return row.row == null ? '—' : row.row;
    },
    // A row that saved but carries a note (e.g. "unit_image_url: download
    // failed … saved without image") is a warning, not an error — amber, so
    // it stands out from plain successes without looking like a failure.
    detailClass(row) {
      if (row.result === 'failed') return 'text-red-600 dark:text-red-400';
      if (row.result !== 'skipped' && row.detail) {
        return 'text-amber-700 dark:text-amber-300';
      }
      return 'text-slate-500 dark:text-slate-400';
    },
  },
};
</script>

<template>
  <div class="space-y-3">
    <div
      v-if="isJobFailed"
      class="rounded-md bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
    >
      <p class="mb-0 font-medium">
        {{ $t('REAL_ESTATE.BULK_IMPORT.JOB_FAILED') }}
      </p>
      <p v-if="job.error" class="mb-0 mt-1 text-xs">{{ job.error }}</p>
    </div>
    <p v-else class="mb-0 text-sm text-slate-700 dark:text-slate-200">
      <span
        v-if="isReplace"
        class="mr-1 inline-flex px-1.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-medium"
      >
        {{ $t('REAL_ESTATE.BULK_IMPORT.MODE_REPLACE') }}
      </span>
      {{
        $t(
          isReplace
            ? 'REAL_ESTATE.BULK_IMPORT.SUMMARY_REPLACE'
            : 'REAL_ESTATE.BULK_IMPORT.SUMMARY',
          {
            created: job.created,
            updated: job.updated,
            skipped: job.skipped || 0,
            deleted: job.deleted || 0,
            failed: job.failed,
          }
        )
      }}
    </p>

    <div
      v-if="warnings.length"
      class="rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
    >
      <p class="mb-1 font-medium">
        {{ $t('REAL_ESTATE.BULK_IMPORT.WARNINGS_TITLE') }}
      </p>
      <ul class="mb-0 list-disc pl-4 space-y-0.5">
        <li v-for="(warning, index) in warnings" :key="index">
          {{ warning }}
        </li>
      </ul>
    </div>

    <div v-if="sortedRows.length" class="max-h-80 overflow-y-auto">
      <table class="w-full text-xs border-collapse">
        <thead
          class="sticky top-0 bg-white dark:bg-slate-900 text-left text-slate-500 dark:text-slate-400"
        >
          <tr class="border-b border-slate-200 dark:border-slate-700">
            <th class="py-1.5 pr-3 font-medium">
              {{ $t('REAL_ESTATE.BULK_IMPORT.COLUMN_SHEET') }}
            </th>
            <th class="py-1.5 pr-3 font-medium">
              {{ $t('REAL_ESTATE.BULK_IMPORT.COLUMN_ROW') }}
            </th>
            <th class="py-1.5 pr-3 font-medium">
              {{ $t('REAL_ESTATE.BULK_IMPORT.COLUMN_ITEM') }}
            </th>
            <th class="py-1.5 pr-3 font-medium">
              {{ $t('REAL_ESTATE.BULK_IMPORT.COLUMN_RESULT') }}
            </th>
            <th class="py-1.5 pr-3 font-medium">
              {{ $t('REAL_ESTATE.BULK_IMPORT.COLUMN_DETAIL') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in sortedRows"
            :key="index"
            class="border-b border-slate-100 dark:border-slate-800 align-top"
          >
            <td class="py-1.5 pr-3 text-slate-600 dark:text-slate-300">
              {{ sheetLabel(row.file) }}
            </td>
            <td class="py-1.5 pr-3 text-slate-600 dark:text-slate-300">
              {{ rowLabel(row) }}
            </td>
            <td class="py-1.5 pr-3 text-slate-900 dark:text-slate-100">
              {{ row.item }}
            </td>
            <td class="py-1.5 pr-3 font-medium">
              <span :class="resultClass(row.result)">
                {{ resultLabel(row.result) }}
              </span>
            </td>
            <td class="py-1.5 pr-3" :class="detailClass(row)">
              {{ row.detail || '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
